import { useState } from 'react';
import type { FormElement } from '../lib/types';

export default function FormBuilder() {
  const [formElements, setFormElements] = useState<FormElement[]>([]);
  const [selectedElement, setSelectedElement] = useState<FormElement | null>(null);

  const elementTypes = [
    { type: 'text', label: 'Text Input', icon: 'ri-text' },
    { type: 'number', label: 'Number', icon: 'ri-hashtag' },
    { type: 'email', label: 'Email', icon: 'ri-mail-line' },
    { type: 'select', label: 'Dropdown', icon: 'ri-arrow-down-s-line' },
    { type: 'checkbox', label: 'Checkbox', icon: 'ri-checkbox-line' },
    { type: 'radio', label: 'Radio', icon: 'ri-radio-button-line' },
    { type: 'textarea', label: 'Text Area', icon: 'ri-file-text-line' },
    { type: 'date', label: 'Date', icon: 'ri-calendar-line' }
  ];

  const addElement = (type: string) => {
    const newElement: FormElement = {
      id: `element-${Date.now()}`,
      type: type as any,
      label: `New ${type} field`,
      required: false
    };
    setFormElements([...formElements, newElement]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Form Builder</h1>
            <p className="text-gray-600">Design responsive forms with drag-and-drop components</p>
          </div>

          <div className="flex items-center space-x-3">
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <i className="ri-eye-line mr-2"></i>
              Preview
            </button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              <i className="ri-save-line mr-2"></i>
              Save Form
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Elements Panel */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Form Elements</h3>
              <div className="space-y-2">
                {elementTypes.map((element) => (
                  <button
                    key={element.type}
                    onClick={() => addElement(element.type)}
                    className="w-full flex items-center px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                  >
                    <i className={`${element.icon} text-blue-600 mr-3`}></i>
                    <span className="text-sm font-medium text-gray-900">{element.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div className="col-span-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Form Canvas</h3>
              <div className="min-h-[600px] border-2 border-dashed border-gray-300 rounded-lg p-6">
                {formElements.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-center">
                    <div>
                      <i className="ri-drag-drop-line text-6xl text-gray-300 mb-4"></i>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Building Your Form</h3>
                      <p className="text-gray-600">Click on elements from the left panel to add them here</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formElements.map((element) => (
                      <div
                        key={element.id}
                        onClick={() => setSelectedElement(element)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          selectedElement?.id === element.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {element.label}
                          {element.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {element.type === 'textarea' ? (
                          <textarea
                            placeholder={element.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            rows={3}
                          />
                        ) : element.type === 'select' ? (
                          <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                            <option>Select an option</option>
                            {element.options?.map((opt, idx) => (
                              <option key={idx}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={element.type}
                            placeholder={element.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Properties Panel */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Properties</h3>
              {selectedElement ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                    <input
                      type="text"
                      value={selectedElement.label}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Placeholder</label>
                    <input
                      type="text"
                      value={selectedElement.placeholder || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedElement.required}
                      className="mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">Required Field</label>
                  </div>
                  <button className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                    <i className="ri-delete-bin-line mr-2"></i>
                    Delete Element
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="ri-settings-line text-4xl text-gray-300 mb-2"></i>
                  <p className="text-sm text-gray-600">Select an element to edit its properties</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
